import {AxiosError} from "axios";
import {ApiConfig} from "components/src/api/admin/ApiConfig";
import {LogApi, LogTarget} from "components/src/api/admin/generated";
import {Pager} from "components/src/common/Pager";
import {RowsPerPage} from "components/src/common/RowsPerPage";
import {useSpinnerStatusContext} from "components/src/hook/UseSpinnerStatusContext";
import {useTranslationWithOverride} from "components/src/hook/UseTranslationWithOverride";
import {PageAlert} from "components/src/layout/PageAlert.tsx";
import {PageContent} from "components/src/layout/PageContent";
import {PageFooter} from "components/src/layout/PageFooter.tsx";
import {PageHeader} from "components/src/layout/PageHeader.tsx";
import {DateUtils} from "components/src/utils/DateUtils";
import {Highlighter} from "components/src/utils/Highlighter";
import {logger} from "components/src/utils/Logger";
import {Utils} from "components/src/utils/Utils";
import {GoogleDocs as LogsIcon} from "iconoir-react";
import {useEffect, useMemo, useState} from 'react'
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import InputGroup from 'react-bootstrap/InputGroup';
import Row from 'react-bootstrap/Row';
import Table from 'react-bootstrap/Table';
import ToggleButton from 'react-bootstrap/ToggleButton';
import {useNavigate} from "react-router-dom";

/*
 * REST api for accessing the back-end
 */
const logApi = new LogApi(ApiConfig.config());

export interface LogLine {
    __REALTIME_TIMESTAMP: string,
    SYSLOG_IDENTIFIER: string
    MESSAGE: string,
    PRIORITY: string,
}

export const LogView = () => {
    /**
     * Navigate is used to navigate to the login page if the user is no longer authenticated or to some other page
     * if the dialog is closed
     */
    const navigate = useNavigate();
    /**
     * The log target to view
     */
    const [logTarget, setLogTarget] = useState<LogTarget>(LogTarget.Mta);
    /**
     * If true, the latest log lines will be shown first
     */
    const [reverse, setReverse] = useState<boolean>(true);
    /**
     * active filter
     */
    const [filter, setFilter] = useState<string>("");
    /**
     * new filter
     */
    const [newFilter, setNewFilter] = useState<string>("");
    /**
     * since field
     */
    const [since, setSince] = useState<string>("");
    /**
     * new since field
     */
    const [newSince, setNewSince] = useState<string>("");
    /**
     * until field
     */
    const [until, setUntil] = useState<string>("");
    /**
     * new until field
     */
    const [newUntil, setNewUntil] = useState<string>("");
    /**
     * log lines count
     */
    const [logLinesCount, setLogLinesCount] = useState<number>(0);
    /**
     * log lines
     */
    const [logLines, setLogLines] = useState<LogLine[]>([]);
    /**
     * The error message if some general error occurred
     */
    const [errorMessage, setErrorMessage] = useState<string>();
    /**
     * The index of the first page to show
     */
    const [currentPage, setCurrentPage] = useState<number>(1);
    /**
     * The max number of domains to show
     */
    const [rowsPerPage, setRowsPerPage] = useState<number>(40);
    /**
     * If true, the items will be reloaded from the back-end
     */
    const [forceReload, setForceReload] = useState<boolean>(false);
    /**
     * Keeps track of activities for which the spinner should be shown. If an activity is started, the counter should
     * be increased by one and after the activity
     */
    const {setSpinnerStateCounter} = useSpinnerStatusContext();
    /**
     * i18next translation hook
     */
    const {t} = useTranslationWithOverride();
    /**
     * Compiled regex for the active filter, or null if the filter is empty or invalid
     */
    const filterRegex = useMemo(() => {
        if (!filter) return null;
        try {
            return new RegExp(filter, 'gi');
        } catch {
            return null;
        }
    }, [filter]);

    useEffect(() =>
    {
        logger.debug("Load log line count")

        // reset state when loading new data
        setForceReload(false);
        setCurrentPage(1);

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        logApi.logControllerGetLogLinesCount(logTarget, filter, since, until)
            .then(response =>
            {
                setLogLinesCount(response.data)
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error getting log line count", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });
    }, [navigate,
        forceReload,
        setSpinnerStateCounter,
        logTarget,
        filter,
        since,
        until,
        t
    ]);

    useEffect(() =>
    {
        logger.debug("Load log lines")

        // reset state when loading new data
        setForceReload(false);

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        logApi.logControllerGetLogLines(logTarget, reverse, (currentPage - 1) * rowsPerPage, rowsPerPage, filter, since, until)
            .then(response =>
            {
                setLogLines(response.data as unknown as LogLine[])
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error getting log lines", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });
    }, [navigate,
        forceReload,
        setSpinnerStateCounter,
        logTarget,
        reverse,
        currentPage,
        rowsPerPage,
        filter,
        since,
        until,
        t
    ]);

    const highlight = (log : string, priority : string) => {

        let result = Highlighter.highlight(log);

        // add additional rules based on the log target
        if (logTarget === LogTarget.Mta)
        {
            result = result
                .replace("Connection refused", "<span class='highlight-danger'>$&</span>")
                .replace(/(warning):/g, "<span class='highlight-danger'>$1</span>:")
                .replace(/^(NOQUEUE):/g, "<span class='highlight-danger'>$1</span>:")
                .replace(/^[a-zA-Z0-9]+:\s/g, "<span class='highlight-info'>$&</span>")
        }
        else if (logTarget === LogTarget.Mpa || logTarget === LogTarget.Portal)
        {
            result = result
                .replace(/(^WARN)\s+/g, "<span class='highlight-danger'>$1</span> ")
                .replace(/(^ERROR)\s+/g, "<span class='highlight-danger'>$1</span> ")

            if (logTarget === LogTarget.Mpa) {
                result = result
                    .replace(/(MailID: )([a-z0-9-]+)/g, "$1<span class='highlight-info'>$2</span>")
                    .replace(/INFO\s+(Received)/g, "INFO <span class='highlight-incoming'>$1</span> ")
            }
        }

        const priorityNumber = parseInt(priority, 10);

        // if priority is warning (4), error (5) or critical (6), highlight the message
        if (!isNaN(priorityNumber) && priorityNumber <= 4) {
            result = result.replace(/(.*)/g, "<span class='highlight-danger'>$1</span>")
        }

        // apply filter highlighting last, alternating between HTML tags (kept as-is)
        // and filter matches (wrapped in a span), so existing markup is not corrupted
        if (filterRegex) {
            const tagOrFilter = new RegExp(`(<[^>]*>)|(${filterRegex.source})`, 'gi');
            result = result.replace(tagOrFilter, (match, tag) =>
                tag !== undefined ? tag : `<span class='highlight-filter'>${match}</span>`
            );
        }

        return result;
    }

    return (
        <div className="d-flex flex-column h-100">
            <PageHeader icon={<LogsIcon />} title={t("logview.title")} onClickReload={() => setForceReload(true)}/>

            <PageAlert message={errorMessage} setMessage={setErrorMessage}/>

            <div className="px-4">
                <Row>
                    <div className="d-flex flex-row align-items-center mb-4">
                        <div className="d-flex justify-content-start">
                            <Form.Select value={logTarget}
                                         onChange={(e) => setLogTarget(Utils.stringToEnum(LogTarget, e.target.value as LogTarget))}>
                                <option value="MTA">{t("logview.mta")}</option>
                                <option value="MPA">{t("logview.mpa")}</option>
                                <option value="PORTAL">{t("logview.portal")}</option>
                                <option value="AUDIT_BACKEND">{t("logview.audit-backend")}</option>
                                <option value="AUDIT_PORTAL">{t("logview.audit-portal")}</option>
                                <option value="IMAP">{t("logview.imap")}</option>
                            </Form.Select>
                        </div>
                        <div className="d-flex flex-fill justify-content-end align-items-center">
                            <div className="d-xl-flex">
                                <ToggleButton
                                    size="sm"
                                    id="toggle-check"
                                    type="checkbox"
                                    variant="outline-secondary"
                                    checked={reverse}
                                    value={1}
                                    onChange={(e) => setReverse(e.target.checked)}
                                >
                                    {t("logview.reverse")}
                                </ToggleButton>


                                <div className="ms-4"/>

                                <InputGroup>
                                    <Form.Control
                                        aria-label="Search value"
                                        type="search"
                                        placeholder={t("logview.search")}
                                        value={newFilter}
                                        onChange={e => setNewFilter(e.target.value)}
                                    />
                                    <Form.Control
                                        aria-label="Since value"
                                        type="search"
                                        placeholder={t("logview.since")}
                                        value={newSince}
                                        onChange={e => setNewSince(e.target.value)}
                                    />
                                    <Form.Control
                                        aria-label="Until value"
                                        type="search"
                                        placeholder={t("logview.until")}
                                        value={newUntil}
                                        onChange={e => setNewUntil(e.target.value)}
                                    />
                                    <Button
                                        variant="primary"
                                        onClick={() => {setFilter(newFilter); setSince(newSince); setUntil(newUntil)}}
                                        disabled={filter === newFilter && since === newSince && until === newUntil}>
                                        {t("logview.search")}
                                    </Button>
                                </InputGroup>
                            </div>
                        </div>
                    </div>
                </Row>
            </div>

            <PageContent className="flex-grow-1 min-h-0">
                <Row className="h-100 w-100">
                    <Col className="h-100 overflow-auto">
                        <Table bordered hover>
                            <thead className="sticky-top">
                            <tr className="text-nowrap">
                                <th>{t("logview.timestamp")}</th>
                                <th>{t("logview.identifier")}</th>
                                <th>{t("logview.message")}</th>
                            </tr>
                            </thead>
                            <tbody>
                            {logLines?.map((ll: LogLine, index) => (
                                <tr key={index}>
                                    <td className={"user-select-all"}> {DateUtils.fromTimestampToLocaleDateString(Number(ll.__REALTIME_TIMESTAMP)/1000)} </td>
                                    <td className={"user-select-all"}> {ll.SYSLOG_IDENTIFIER} </td>
                                    {/*
                                        use MESSAGE.toString() because sometimes the result is not a string
                                        this can happen if "invalid" log lines are written to the journal
                                    */}
                                    <td> <div dangerouslySetInnerHTML={{__html: highlight(ll.MESSAGE?.toString(), ll.PRIORITY)}} /> </td>
                                </tr>
                            ))}
                            </tbody>
                        </Table>
                    </Col>
                </Row>
            </PageContent>

            <PageFooter
                left={<RowsPerPage rowsPerPage={rowsPerPage} setRowsPerPage={setRowsPerPage} setCurrentPage={setCurrentPage}/>}
                right={<Pager itemCount={logLinesCount ?? 0}
                              currentPage={currentPage}
                              setCurrentPage={setCurrentPage}
                              maxItemsPerPage={rowsPerPage}
                />}
            />
        </div>
    );
}
